export type MediaFormat = "video" | "audio"
export type MediaSection = "studio-visit" | "beyond-the-blocks" | "desert-dialogues"

export interface MediaPlatformLink {
  label: string
  url: string
}

export interface MediaShow {
  id: MediaSection
  title: string
  shortTitle: string
  description: string
  links: MediaPlatformLink[]
}

export interface MediaEpisode {
  slug: string
  section: MediaSection
  seriesTitle: string
  title: string
  guest: string
  date: string
  duration?: string
  format: MediaFormat
  youtubeId?: string
  externalUrl: string
  description: string
}

export const MEDIA_SHOWS: MediaShow[] = [
  {
    id: "studio-visit",
    title: "Studio Visit w/ r4v3n",
    shortTitle: "Studio Visit",
    description:
      "Long-form conversations with artists, collectors, builders, and people shaping digital and generative art culture.",
    links: [
      { label: "YouTube", url: "https://www.youtube.com/@r4v3n-art" },
      { label: "Apple Podcasts", url: "https://podcasts.apple.com/us/podcast/studio-visit-with-r4v3n/id1739533717" },
      { label: "Spotify", url: "https://open.spotify.com/show/56d9h9ynErt96bdmkHgCfE" },
      { label: "Amazon Music", url: "https://music.amazon.com/podcasts/1f1a6c3a-816b-467f-9b9b-8e1a2bcfdc7f/studio-visit-with-r4v3n" },
      { label: "RSS", url: "https://feeds.buzzsprout.com/2327351.rss" },
    ],
  },
  {
    id: "beyond-the-blocks",
    title: "Beyond the Blocks with Art Blocks",
    shortTitle: "Beyond the Blocks",
    description:
      "A video series with Art Blocks that expands the conversation around releases, physical practice, displays, and generative systems.",
    links: [
      { label: "Art Blocks YouTube", url: "https://www.youtube.com/@ArtBlocksOfficial" },
      { label: "r4v3n YouTube", url: "https://www.youtube.com/@r4v3n-art" },
    ],
  },
]

export const MEDIA_EPISODES: MediaEpisode[] = [
  {
    slug: "ian-rogers",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Ian Rogers - CXO Ledger",
    guest: "Ian Rogers",
    date: "January 31, 2025",
    duration: "53 min",
    format: "video",
    youtubeId: "E-h3XhIu5ug",
    externalUrl: "https://www.youtube.com/watch?v=E-h3XhIu5ug",
    description:
      "A conversation with Ledger CXO Ian Rogers on programming, collecting blockchain-based art, early internet culture, music, and a career spanning Beastie Boys, Winamp, Yahoo, Apple, LVMH, and Ledger.",
  },
  {
    slug: "ben-skaar",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Ben Skaar - Travel Photographer",
    guest: "Ben Skaar",
    date: "June 28, 2024",
    duration: "91 min",
    format: "video",
    youtubeId: "b_EWLZSxr9o",
    externalUrl: "https://www.youtube.com/watch?v=b_EWLZSxr9o",
    description:
      "Photographer and videographer Ben Skaar joins r4v3n to talk about travel, image-making, creative discipline, and building a photographic practice from a young age.",
  },
  {
    slug: "rafael-rozendaal",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Rafaël Rozendaal - Internet Art Pioneer",
    guest: "Rafaël Rozendaal",
    date: "June 12, 2024",
    duration: "99 min",
    format: "video",
    youtubeId: "96_PikXsj7Q",
    externalUrl: "https://www.youtube.com/watch?v=96_PikXsj7Q",
    description:
      "An in-depth visit with Rafaël Rozendaal on internet art, public screens, institutional collections, and building a practice across browsers, galleries, and daily life.",
  },
  {
    slug: "rainer-hosch",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Rainer Hosch - Photographer to the Stars",
    guest: "Rainer Hosch",
    date: "June 5, 2024",
    duration: "92 min",
    format: "video",
    youtubeId: "0XOxucdNPww",
    externalUrl: "https://www.youtube.com/watch?v=0XOxucdNPww",
    description:
      "Photographer Rainer Hosch discusses portraiture, documentary photography, analog roots, digital art, and carrying forward a family legacy in image-making.",
  },
  {
    slug: "sarah-ridgley",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Sarah Ridgley",
    guest: "Sarah Ridgley",
    date: "May 21, 2024",
    duration: "76 min",
    format: "video",
    youtubeId: "tDI_heLxXKI",
    externalUrl: "https://www.youtube.com/watch?v=tDI_heLxXKI",
    description:
      "Sarah Ridgley joins r4v3n to discuss pixels as material, algorithmic brushwork, p5.js, and the balance between ordered systems and generative surprise.",
  },
  {
    slug: "mountvitruvius",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "MountVitruvius",
    guest: "Anthony Hiley-Mann",
    date: "May 14, 2024",
    duration: "113 min",
    format: "video",
    youtubeId: "4nAcmorgTsg",
    externalUrl: "https://www.youtube.com/watch?v=4nAcmorgTsg",
    description:
      "Anthony Hiley-Mann, also known as MountVitruvius, joins for a long-form studio conversation about generative art, systems, and process.",
  },
  {
    slug: "brett-sylvia",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Brett Sylvia",
    guest: "Brett Sylvia",
    date: "May 6, 2024",
    duration: "85 min",
    format: "video",
    youtubeId: "9lDD9lu3qWg",
    externalUrl: "https://www.youtube.com/watch?v=9lDD9lu3qWg",
    description:
      "A conversation with photographer and videographer Brett Sylvia on practice, family, athletics, music, and finding beauty through the camera.",
  },
  {
    slug: "dca",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "DCA",
    guest: "Daniel Calderon Arenas",
    date: "April 24, 2024",
    duration: "85 min",
    format: "video",
    youtubeId: "XfmDK6EeW6Q",
    externalUrl: "https://www.youtube.com/watch?v=XfmDK6EeW6Q",
    description:
      "DCA discusses generative art, teaching code, natural systems, language, meaning, randomness, and color through an artist's lens.",
  },
  {
    slug: "pete-halvorsen",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Pete Halvorsen",
    guest: "Pete Halvorsen",
    date: "April 18, 2024",
    duration: "102 min",
    format: "video",
    youtubeId: "snhZcesC4ac",
    externalUrl: "https://www.youtube.com/watch?v=snhZcesC4ac",
    description:
      "Photographer Pete Halvorsen talks about movement, travel, commercial practice, social media, and the narrative energy inside a still image.",
  },
  {
    slug: "generative-artworks",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Generative Artworks",
    guest: "Generative Artworks",
    date: "April 5, 2024",
    duration: "70 min",
    format: "video",
    youtubeId: "-W0FnjqZHGQ",
    externalUrl: "https://www.youtube.com/watch?v=-W0FnjqZHGQ",
    description:
      "Generative Artworks discuss Art Blocks Factory, plotters, 3D printing, embroidery, and moving generative systems between digital and physical forms.",
  },
  {
    slug: "fingacode",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "fingacode",
    guest: "fingacode",
    date: "March 27, 2024",
    duration: "54 min",
    format: "video",
    youtubeId: "RKt6VCrDY20",
    externalUrl: "https://www.youtube.com/watch?v=RKt6VCrDY20",
    description:
      "fingacode joins r4v3n to talk about computational creativity, African textile inspiration, custom tools, rhythm, color, and generative visual systems.",
  },
  {
    slug: "lovid",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "LoVid",
    guest: "LoVid",
    date: "March 15, 2024",
    duration: "52 min",
    format: "video",
    youtubeId: "4mtIBPjIals",
    externalUrl: "https://www.youtube.com/watch?v=4mtIBPjIals",
    description:
      "LoVid discuss two decades of interdisciplinary work across video, net art, performance, textile, analog synths, and digital culture.",
  },
  {
    slug: "harvey-rayner",
    section: "studio-visit",
    seriesTitle: "Studio Visit w/ r4v3n",
    title: "Harvey Rayner",
    guest: "Harvey Rayner",
    date: "March 12, 2024",
    duration: "97 min",
    format: "video",
    youtubeId: "GeIlHByg4DI",
    externalUrl: "https://www.youtube.com/watch?v=GeIlHByg4DI",
    description:
      "Harvey Rayner joins the first Studio Visit episode to discuss a long creative coding practice, Art Blocks, invention, design, and daily artistic discipline.",
  },
  {
    slug: "beyond-the-blocks-tigerbob-mystery-garden",
    section: "beyond-the-blocks",
    seriesTitle: "Beyond the Blocks with Art Blocks",
    title: "Studio Release: Tigerbob Mystery Garden",
    guest: "Gossamer and r4v3n",
    date: "Episode 07",
    format: "video",
    youtubeId: "cXjTnQ_2Jac",
    externalUrl: "https://www.youtube.com/watch?v=cXjTnQ_2Jac",
    description:
      "A Beyond the Blocks studio release conversation with Gossamer and r4v3n around Tigerbob Mystery Garden and the context around the work.",
  },
  {
    slug: "beyond-the-blocks-amplitudes-of-canvas",
    section: "beyond-the-blocks",
    seriesTitle: "Beyond the Blocks with Art Blocks",
    title: "Studio Release: Amplitudes of Canvas",
    guest: "Harvey Rayner and r4v3n",
    date: "Episode 06",
    format: "video",
    youtubeId: "Pv4iwmoAM0U",
    externalUrl: "https://www.youtube.com/watch?v=Pv4iwmoAM0U",
    description:
      "Harvey Rayner and r4v3n discuss Amplitudes of Canvas in a Beyond the Blocks studio release conversation for Art Blocks.",
  },
  {
    slug: "beyond-the-blocks-digital-art-physical-space",
    section: "beyond-the-blocks",
    seriesTitle: "Beyond the Blocks with Art Blocks",
    title: "Displaying Digital Art in Physical Space",
    guest: "LoVid and Rafaël Rozendaal",
    date: "Episode 04",
    format: "video",
    youtubeId: "EzvMzrQ_6dA",
    externalUrl: "https://www.youtube.com/watch?v=EzvMzrQ_6dA",
    description:
      "LoVid, Rafaël Rozendaal, and r4v3n talk about screens, display, installation, and how digital artworks live in physical space.",
  },
  {
    slug: "beyond-the-blocks-busy",
    section: "beyond-the-blocks",
    seriesTitle: "Beyond the Blocks with Art Blocks",
    title: "Inside BUSY and Plotting Generative Art",
    guest: "James Merrill and r4v3n",
    date: "Episode 1.5",
    format: "video",
    youtubeId: "DPxeGKq2Yws",
    externalUrl: "https://www.youtube.com/watch?v=DPxeGKq2Yws",
    description:
      "A Beyond the Blocks conversation with James Merrill and r4v3n about BUSY, plotting, and generative art as a physical and computational practice.",
  },
  {
    slug: "beyond-the-blocks-generative-art-physical",
    section: "beyond-the-blocks",
    seriesTitle: "Beyond the Blocks with Art Blocks",
    title: "Making Generative Art Physical",
    guest: "Snowfro, Luke Shannon, and Ismail Tazi S",
    date: "Episode 01",
    format: "video",
    youtubeId: "7RCzEVthpxs",
    externalUrl: "https://www.youtube.com/watch?v=7RCzEVthpxs",
    description:
      "A conversation on taking generative art into physical form with Snowfro, Luke Shannon, Ismail Tazi S, and r4v3n.",
  },
  {
    slug: "beyond-the-blocks-gas-wars",
    section: "beyond-the-blocks",
    seriesTitle: "Beyond the Blocks with Art Blocks",
    title: "Jack Butcher's Gas Wars",
    guest: "Jack Butcher and r4v3n",
    date: "Special",
    format: "video",
    youtubeId: "C7j3IrMh764",
    externalUrl: "https://www.youtube.com/watch?v=C7j3IrMh764",
    description:
      "A Beyond the Blocks conversation around Jack Butcher's Gas Wars, hosted with r4v3n.",
  },
  {
    slug: "desert-dialogues-echoes-of-frida",
    section: "desert-dialogues",
    seriesTitle: "Desert Dialogues",
    title: "Echoes of Frida",
    guest: "Anna Carreras and Jimena Buena Vida",
    date: "December 6, 2024",
    duration: "32 min",
    format: "video",
    youtubeId: "gb0xte4OmYE",
    externalUrl: "https://www.youtube.com/watch?v=gb0xte4OmYE",
    description:
      "Recorded in Marfa during Art Blocks Weekend 2024, this Desert Dialogues conversation brings together Anna Carreras and Jimena Buena Vida around Echoes of Frida.",
  },
  {
    slug: "desert-dialogues-art-blocks-day-zero",
    section: "desert-dialogues",
    seriesTitle: "Desert Dialogues",
    title: "Art Blocks Day 0 Artists",
    guest: "Snowfro, DCA, and Jeff Davis",
    date: "December 4, 2024",
    duration: "28 min",
    format: "video",
    youtubeId: "OQ6pjnMFjZE",
    externalUrl: "https://www.youtube.com/watch?v=OQ6pjnMFjZE",
    description:
      "Snowfro, DCA, Jeff Davis, and r4v3n sit down in Marfa to discuss the history of Art Blocks and the future of on-chain generative art.",
  },
]

export const getMediaShow = (id: MediaSection) =>
  MEDIA_SHOWS.find((show) => show.id === id)

export const getMediaEpisode = (slug: string | undefined) =>
  MEDIA_EPISODES.find((episode) => episode.slug === slug)

export const getMediaEpisodesBySection = (section: MediaSection) =>
  MEDIA_EPISODES.filter((episode) => episode.section === section)
